import PropTypes from 'prop-types';
import React from 'react';
import toastr from 'toastr';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Button } from '@src/components/ui/button/button';
import { cn } from '@src/utils/ui';
import { SubmitButton } from '@src/components/ui/react-hook-form/submit-button/submit-button';
import { Form } from '@src/components/ui/react-hook-form/form/form';
import { TextAreaInput } from '@src/components/ui/react-hook-form/textarea-input/textarea-input';
import { AutoCompleteInput } from '@src/components/ui/react-hook-form/autocomplete-input/autocomplete-input';
import { z } from 'zod';
import { useFormContext } from 'react-hook-form';

const LEAVE_REVIEW_VALIDATOR = z.object({
    ItemRating: z.number({
        required_error: 'Field cannot be empty.',
        invalid_type_error: 'Field cannot be empty.'
    }),
    Message: z
        .string({ required_error: 'Field cannot be empty.' })
        .min(1, 'Field cannot be empty.')
        .max(400, 'You’ve reached the maximum limit of 400 characters.')
});

export const LeaveReviewModal = ({
    open,
    onClose,
    orderId,
    itemId,
    cartId,
    onChange,
    fulfillmentStatus,
    submitFeedbackForCartItem
}) => {
    const handleClose = (e, reason) => {
        e.preventDefault();
        e.stopPropagation();
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            onClose(_event);
        }
    };

    const handleSubmit = async values => {
        await new Promise(resolve => {
            submitFeedbackForCartItem(
                { ...values, id: orderId, itemId, cartId, fulfillmentStatus },
                ({ data, error }) => {
                    if (data) {
                        toastr.success(data.message);
                        if (onChange) onChange();
                    }

                    if (error) {
                        console.error('leave review error:', error);
                        toastr.error(error.message || 'Oops! Something went wrong.');
                    }

                    resolve();
                }
            );
        });
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: '600px',
                    maxWidth: '90%'
                }
            }}
        >
            <Form onSubmit={handleSubmit} validator={{ schema: LEAVE_REVIEW_VALIDATOR }}>
                <DialogContent className='tw-space-y-[16px]'>
                    <div className='tw-text-[20px] tw-font-medium'>How was this Tasker?</div>
                    <AutoCompleteInput
                        name='ItemRating'
                        label='Select Rating'
                        options={[
                            { label: '5 Stars', value: 5 },
                            { label: '4 Stars', value: 4 },
                            { label: '3 Stars', value: 3 },
                            { label: '2 Stars', value: 2 },
                            { label: '1 Stars', value: 1 }
                        ]}
                        placeholder='Per Hour'
                        className='tw-w-full'
                        InputProps={{
                            className: 'tw-text-[16px] tw-bg-white'
                        }}
                    />
                    <div>
                        <span className='tw-font-medium'>Write a review</span>
                        <TextAreaInput
                            name='Message'
                            minRows={3}
                            placeholder='What should other customers know about this Tasker?'
                            className='tw-w-full'
                        />
                    </div>
                </DialogContent>
                <Buttons onClose={onClose} />
            </Form>
        </Dialog>
    );
};

const Buttons = ({ onClose }) => {
    const { formState } = useFormContext();

    return (
        <DialogActions>
            <Button
                onClick={onClose}
                className={cn('tw-p-[8px] tw-text-[14px]')}
                variant='text'
                disabled={formState.isSubmitting}
            >
                CANCEL
            </Button>
            <SubmitButton type='button' className={cn('tw-p-[8px] tw-text-[14px]')} variant='contained'>
                SUBMIT REVIEW
            </SubmitButton>
        </DialogActions>
    );
};

LeaveReviewModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    orderId: PropTypes.string.isRequired,
    itemId: PropTypes.string.isRequired,
    cartId: PropTypes.string.isRequired,
    submitFeedbackForCartItem: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired
};
Buttons.propTypes = LeaveReviewModal.propTypes;
